import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GuideArticleSection,
  GuideKeyTakeaways,
  GuideLayout,
} from "@/components/guides/GuideLayout";
import { BarcodeFigure } from "@/components/guides/BarcodeFigure";
import { Callout } from "@/components/guides/Callout";
import { Compare } from "@/components/guides/Compare";
import { TryTool } from "@/components/guides/TryTool";
import { TableScroll } from "@/components/layout/ContentBlock";
import { FaqSection } from "@/components/seo/FaqSection";
import { GUIDES_BY_SLUG } from "@/lib/guides";
import { pageHead, breadcrumbJsonLd, ldScript, absoluteUrl } from "@/lib/seo";

const GUIDE = GUIDES_BY_SLUG["code-128-explained"];

const TOC = [
  { id: "the-short-answer", label: "The short answer" },
  { id: "at-a-glance", label: "Code 128 at a glance" },
  { id: "subsets", label: "The three subsets" },
  { id: "fnc1", label: "FNC1 and GS1-128" },
  { id: "worked-example", label: "A worked GS1-128 example" },
  { id: "where-you-see-it", label: "Where you actually see it" },
  { id: "decoding", label: "Decoding Code 128 in software" },
  { id: "common-mistakes", label: "Common mistakes" },
  { id: "quick-decision", label: "Quick decision guide" },
  { id: "code-128-vs-code-39", label: "Code 128 vs Code 39" },
  { id: "examples", label: "Code 128 examples" },
  { id: "references", label: "References" },
];

const FAQ = [
  {
    q: "Why are there three Code 128 subsets?",
    a: "Subsets A, B and C let Code 128 encode the full ASCII range efficiently. Subset A covers ASCII 0–95 (uppercase + control codes), B covers 32–127 (uppercase + lowercase + punctuation), and C encodes pairs of digits in a single symbol character — halving the symbol width for numeric data. Encoders switch between subsets mid-symbol using shift characters.",
  },
  {
    q: "What Is GS1-128 and How Is It Different?",
    a: "GS1-128 (formerly UCC/EAN-128) is Code 128 with a mandatory FNC1 as the first symbol character, signalling that the payload follows GS1 Application Identifier syntax. Same barcode symbology underneath, with stricter parsing rules layered on top.",
  },
  {
    q: "What is FNC1 actually for?",
    a: "FNC1 plays two roles inside a GS1-128. The first FNC1 immediately after the start character marks the symbol as a GS1 carrier rather than a plain Code 128. Subsequent FNC1 characters act as separators between variable-length Application Identifiers — so the parser knows where AI (10) batch ends and the next AI begins.",
  },
  {
    q: "Why does my Code 128 scanner output extra characters?",
    a: "Usually it's the FNC1 separator being transmitted as a control character — most commonly ASCII 29 (GS). Configure your scanner or middleware to translate FNC1 into a delimiter your software can split on. The same trick lets you parse variable-length AIs without ambiguity.",
  },
  {
    q: "Is Code 128 used at retail POS?",
    a: "Rarely on consumer packaging — retail POS expects EAN or UPC. Code 128 dominates the back end: shipping labels, pallet tags, internal asset stickers and the GS1 logistics label. It's a workhorse of the warehouse, not the shelf.",
  },
  {
    q: "How long can a Code 128 barcode be?",
    a: "There's no hard maximum, but practical limits kick in around 30–80 characters because the symbol gets too wide to scan reliably with a typical laser. For longer payloads, switch to a 2D format like Data Matrix or QR.",
  },
  {
    q: "Does Code 128 Have a Check Digit?",
    a: "Yes. Every Code 128 symbol carries a mandatory check character, calculated modulo 103 from the start character and each data character weighted by its position. The scanner verifies it and drops it, so it never shows in the decoded text or the human-readable line. Data inside a GS1-128, such as a GTIN or an SSCC, still carries its own mod-10 check digit on top.",
  },
  {
    q: "Can Code 128 Have Letters?",
    a: "Yes. Subset A covers uppercase letters and control characters, Subset B adds lowercase letters and punctuation, and the encoder switches between them as the text requires. The standard also reaches characters 128 to 255 through the FNC4 function character, but most systems stay with printable ASCII, which is also all the ReadBarcode generators accept.",
  },
];

export const Route = createFileRoute("/barcode-guides/code-128-explained")({
  component: Code128Guide,
  head: () => {
    const base = pageHead({
      title: GUIDE.title,
      description: GUIDE.description,
      path: GUIDE.path,
      ogType: "article",
    });
    return {
      ...base,
      scripts: [
        ldScript({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: GUIDE.title,
          description: GUIDE.description,
          mainEntityOfPage: absoluteUrl(GUIDE.path),
          author: { "@type": "Organization", name: "ReadBarcode" },
          publisher: { "@type": "Organization", name: "ReadBarcode" },
          datePublished: GUIDE.lastUpdated,
          dateModified: GUIDE.lastUpdated,
          about: GUIDE.keyword,
        }),
        ldScript(
          breadcrumbJsonLd([
            { name: "Home", path: "/" },
            { name: "Barcode Guides", path: "/barcode-guides" },
            { name: GUIDE.shortTitle, path: GUIDE.path },
          ]),
        ),
      ],
    };
  },
});

function Code128Guide() {
  return (
    <GuideLayout
      guide={GUIDE}
      toc={TOC}
      nextRead={[
        {
          to: "/barcode-guides/1d-vs-2d-barcodes",
          label: "1D vs 2D barcodes",
          desc: "When a wider Code 128 belongs and when to move the data into a 2D code.",
        },
        {
          to: "/barcode-guides/barcode-formats",
          label: "Barcode formats field guide",
          desc: "Where Code 128 and GS1-128 fit among UPC, EAN, ITF-14 and the 2D family.",
        },
        {
          to: "/barcode-generator",
          label: "Barcode Generator",
          desc: "Make a single Code 128 or Code 39 barcode and download it as PNG or SVG.",
        },
        {
          to: "/bulk-barcode-generator",
          label: "Bulk Barcode Generator",
          desc: "Turn a list of up to 200 values into Code 128 labels in one pass.",
        },
      ]}
    >
      <p>
        A pallet rolls into the dock. Across the front face is a single 1D barcode about the width
        of a forearm. The receiving system scans it and you can see the payload arrive in the
        warehouse software: <code>0107640156790009ABC123 26013 1 4225</code>. Half of those
        characters belong to the GTIN, half to the batch number, the rest are an expiry and a serial
        — but the parser has glued them together because the FNC1 separators between Application
        Identifiers were dropped on the way through the scanner driver. The barcode is perfectly
        correct. The configuration around it isn&apos;t.
      </p>
      <p>
        That&apos;s a typical encounter with <strong>Code 128</strong> — the workhorse 1D symbology
        behind almost every shipping label, pallet tag and asset sticker in logistics. It encodes
        the full 128-character ASCII range in a compact form, switches between three subsets to pack
        numeric data tighter, and gains a structured-data flavour as <strong>GS1-128</strong> when
        it carries an <strong>FNC1</strong> at the head. This guide explains <strong>Code 128 explained</strong> at a level you can act on: the three subsets, the FNC1
        modifier, a worked GS1-128 with every byte labelled, the scanner configuration that bites
        people first, and how to tell <strong>Code 128 vs Code 39</strong> when you only have the
        printed value in front of you.
      </p>

      <GuideArticleSection id="the-short-answer" title="The Short Answer">
        <p>
          Code 128 is a 1D barcode that encodes all 128 ASCII characters, used almost exclusively in
          logistics and asset tracking rather than retail POS. It picks between three internal
          subsets (A, B and C) to keep the symbol as narrow as possible. When the first character is
          an FNC1, the same barcode is called GS1-128 and the payload is read as GS1 Application
          Identifiers — GTIN, batch, expiry, serial, pallet number.
        </p>
        <Compare
          panes={[
            {
              label: "Code 128",
              caption: "Plain Code 128 — any ASCII payload, no GS1 parsing.",
              children: (
                <BarcodeFigure
                  format="code128"
                  value="SHIP-2026-00042"
                  formatLabel="Code 128"
                  caption={
                    <>
                      Internal asset label encoded as Code 128, Subset B (mixed-case ASCII): <code>SHIP-2026-00042</code>.
                    </>
                  }
                />
              ),
            },
            {
              label: "GS1-128",
              caption: "Code 128 with FNC1 — payload follows GS1 AI syntax.",
              children: (
                <BarcodeFigure
                  format="gs1128"
                  value="(00)103456789012345677"
                  formatLabel="GS1-128"
                  caption={
                    <>
                      Pallet-level SSCC encoded as a GS1-128. The bars start with FNC1; the readable
                      text below shows the AI <code>(00)</code> followed by the 18-digit serial.
                    </>
                  }
                />
              ),
            },
          ]}
        />
      </GuideArticleSection>

      <GuideArticleSection id="at-a-glance" title="Code 128 at a glance">
        <p>
          Where Code 128 fits depends on what else you considered. Most teams pick between Code 128,
          Code 39 and GS1-128 — they look similar at a glance and the wrong choice surfaces months
          later when the data won&apos;t parse cleanly.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              Side-by-side comparison of Code 128, Code 39 and GS1-128 — character set, density,
              lowercase support, FNC1 handling and typical use case.
            </caption>
            <thead>
              <tr>
                <th scope="col">
                  Aspect
                </th>
                <th scope="col">
                  Code 128
                </th>
                <th scope="col">
                  Code 39
                </th>
                <th scope="col">
                  GS1-128
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">
                  Character set
                </th>
                <td>All 128 ASCII characters</td>
                <td>
                  Uppercase + digits + 7 symbols (43 total)
                </td>
                <td>
                  Subset of Code 128 with GS1 AIs only
                </td>
              </tr>
              <tr>
                <th scope="row">
                  Lowercase
                </th>
                <td>Yes (via Subset B)</td>
                <td>No</td>
                <td>Yes (inside AI 10, 21, etc.)</td>
              </tr>
              <tr>
                <th scope="row">
                  Density
                </th>
                <td>
                  High; double-density for pure numeric (Subset C)
                </td>
                <td>
                  Low — about 2× wider than Code 128 for the same payload
                </td>
                <td>High; same as Code 128</td>
              </tr>
              <tr>
                <th scope="row">
                  Structured data
                </th>
                <td>No (free-form ASCII)</td>
                <td>No (free-form, upper only)</td>
                <td>
                  Yes — GS1 Application Identifiers separated by FNC1
                </td>
              </tr>
              <tr>
                <th scope="row">
                  FNC1
                </th>
                <td>Optional</td>
                <td>Not supported</td>
                <td>Mandatory as first character</td>
              </tr>
              <tr>
                <th scope="row">
                  Where you see it
                </th>
                <td>
                  Shipping labels, asset stickers, hospital wristbands
                </td>
                <td>
                  Legacy automotive, defence, library catalogues
                </td>
                <td>
                  GS1 logistics labels, SSCC pallet IDs, traceability data
                </td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
      </GuideArticleSection>

      <GuideArticleSection id="subsets" title="The Three Subsets: A, B and C">
        <p>
          The clever part of Code 128 is that it doesn&apos;t encode characters one at a time.
          Instead it carries an internal state that selects one of three subsets and switches
          between them mid-symbol with a single shift character. The encoder picks whichever subset
          makes the next few characters cheapest, and the decoded text comes out as a clean ASCII
          string with no trace of the underlying gymnastics.
        </p>
        <ul>
          <li>
            <strong>Subset A</strong> — ASCII 0 through 95: uppercase letters, digits and control
            codes (NUL, GS, RS, FS and the rest). Used mainly in legacy systems that need to embed
            control characters in the payload.
          </li>
          <li>
            <strong>Subset B</strong> — ASCII 32 through 127: uppercase, lowercase, digits and
            punctuation. The default for general-purpose alphanumeric text — &quot;Code 128 supports
            lowercase&quot; means Subset B.
          </li>
          <li>
            <strong>Subset C</strong> — pairs of digits from <code>00</code> to <code>99</code> encoded as one symbol character. The killer feature for pure numeric payloads: a
            12-digit value takes six Subset C characters instead of twelve Subset B characters,
            halving the printed symbol width.
          </li>
        </ul>
        <Callout variant="in-practice" title="Why Code C matters for GS1-128">
          <p>
            Almost every GS1 Application Identifier — GTIN (14 digits), SSCC (18 digits), production
            date (6 digits), serial (variable) — is heavily numeric. GS1-128 encoders aggressively
            switch to Subset C wherever they see two-digit runs, which is the only thing that makes
            long structured payloads fit on a 100&nbsp;mm shipping label.
          </p>
        </Callout>
        <p>
          You never have to think about subsets when decoding — the scanner returns plain ASCII
          either way. The reason to know about them is when you&apos;re building a Code 128 from
          scratch and the symbol comes out wider than expected: usually a numeric run that the
          encoder failed to fold into Subset C.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="fnc1" title="FNC1 and GS1-128">
        <p>
          FNC1 is the function character that turns a plain Code 128 into a GS1-128. It plays two
          roles:
        </p>
        <ol>
          <li>
            <strong>Mode flag.</strong> An FNC1 immediately after the Code 128 start character marks
            the entire symbol as a GS1 carrier. Without that flag the payload is read as free-form
            ASCII; with it, parsers expect GS1 Application Identifiers.
          </li>
          <li>
            <strong>Variable-length separator.</strong> Some AIs are fixed length (GTIN is always 14
            digits inside AI 01; the expiry date inside AI 17 is always 6 digits). Others — batch
            (10), serial (21), customer reference (240) — are variable up to their maximum. The
            encoder appends an FNC1 immediately after a variable-length AI so the parser knows the
            field has ended. Fixed-length AIs don&apos;t need a trailing FNC1 because their length
            is known from the AI itself.
          </li>
        </ol>
        <Callout variant="spec" title="FNC1 on the wire">
          <p>
            FNC1 isn&apos;t a printable character. Most scanners transmit it as ASCII 29 (
            <code>GS</code>, the &quot;group separator&quot;); some send it as a configured
            replacement (e.g. <code>|</code> or <code>^</code>). The bracketed form <code>(01)…(10)…</code> you see in spec sheets and on this site is a human-readable
            convention — the brackets are <em>not</em> encoded in the bars; they map to FNC1 markers
            in the symbol and to the AI table during parsing.
          </p>
        </Callout>
      </GuideArticleSection>

      <GuideArticleSection id="worked-example" title="A Worked GS1-128 Example">
        <p>
          Take the worked example used throughout the GS1 logistics guides: <code>(01)07640156790009(10)ABC123(17)261231</code>. It carries a GTIN, a batch number and
          an expiry date in a single GS1-128 symbol.
        </p>
        <BarcodeFigure
          format="gs1128"
          value="(01)07640156790009(10)ABC123(17)261231"
          formatLabel="GS1-128"
          caption={
            <>
              GS1-128 encoding GTIN <code>07640156790009</code>, batch <code>ABC123</code>, expiry <code>2026-12-31</code> — three AIs, one symbol.
            </>
          }
        />
        <p>Reading the symbol left-to-right, the parser walks through it like this:</p>
        <ol>
          <li>
            <strong>Start C + FNC1.</strong> The encoder chose Subset C because the payload opens
            with a long numeric run. The FNC1 immediately after the start character marks the symbol
            as GS1-128.
          </li>
          <li>
            <strong>AI (01) — GTIN-14.</strong> Fixed length: 14 digits. <code>07640156790009</code> is the product identifier. No trailing FNC1 needed because GS1 knows AI 01 is exactly 14
            digits.
          </li>
          <li>
            <strong>AI (10) — Batch.</strong> Variable length, up to 20 alphanumeric characters. The
            encoder switched out of Subset C into Subset B for <code>ABC123</code>, then appended an
            FNC1 to mark the end of the variable field.
          </li>
          <li>
            <strong>AI (17) — Expiry.</strong> Fixed length: 6 digits (YYMMDD). <code>261231</code> means 31 December 2026.
          </li>
          <li>
            <strong>Check digit + stop.</strong> Code 128 finishes with a modulo-103 checksum and a
            stop pattern, both invisible in the decoded ASCII.
          </li>
        </ol>
        <Callout variant="heads-up" title="Order matters less than you think">
          <p>
            GS1 doesn&apos;t mandate a specific AI order inside a GS1-128. The example above could
            equally be <code>(01)07640156790009(17)261231(10)ABC123</code> — same data, same valid
            symbol. What matters is that variable-length AIs (10, 21, 240, …) are followed by an
            FNC1 separator. Put a variable AI before a fixed one without the FNC1 and the parser
            swallows the next AI&apos;s digits as part of the batch.
          </p>
        </Callout>
        <p>
          Paste the same value into the decoder and you can see each AI laid out with its spec name
          and validation status:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Digital Link Decoder"
          description="Decode any GS1-128 element string into structured GTIN + batch + expiry data."
          value="(01)07640156790009(10)ABC123(17)261231"
          cta="Decode the worked example"
        />
      </GuideArticleSection>

      <GuideArticleSection id="where-you-see-it" title="Where You Actually See It">
        <p>
          Code 128 is the dominant 1D symbology of the back-of-house. You meet it everywhere a
          workflow needs to encode more than a GTIN but doesn&apos;t justify a 2D code:
        </p>
        <ul>
          <li>
            <strong>Shipping labels.</strong> Tracking numbers from FedEx, UPS, DHL and Royal Mail
            are almost always Code 128 — alphanumeric, predictable length, easy to print on a
            thermal label.
          </li>
          <li>
            <strong>Pallet and case tags.</strong> The GS1 logistics label uses GS1-128 to carry the
            SSCC (AI 00) and, where required, the GTIN of the case contents (AI 02) plus batch and
            expiry.
          </li>
          <li>
            <strong>Asset and inventory stickers.</strong> Internal labels for equipment, shelves
            and reusable containers — &quot;ASSET-2026-00042&quot; style — sit on plain Code 128
            with no GS1 layer.
          </li>
          <li>
            <strong>Healthcare wristbands.</strong> Patient ID bands often use Code 128 for its
            lowercase support and its ability to fit twenty-plus characters on a 10&nbsp;mm-tall
            strip.
          </li>
          <li>
            <strong>Library and archive catalogues.</strong> Some legacy systems still emit Code 39
            here; modern ones moved to Code 128 for the density.
          </li>
        </ul>
        <p>
          To roll out new labels yourself, the <Link to="/barcode-generator">Barcode Generator</Link> covers plain Code 128 with the
          encoding controls exposed; the <Link to="/itf-14-label-generator">ITF-14 Carton Label Generator</Link> is the right tool
          when the carton-level GTIN is the main subject.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="decoding" title="Decoding Code 128 in software">
        <p>
          The most common Code 128 bug isn&apos;t in the barcode — it&apos;s in the configuration
          between the scanner and the application that receives the data. Three settings cover most
          of the failures:
        </p>
        <ol>
          <li>
            <strong>FNC1 transmission.</strong> Configure the scanner to emit FNC1 as ASCII 29 (
            <code>GS</code>) — the GS1 default. Some scanners are set to emit FNC1 as a blank
            character, which is what destroys the example at the top of this guide.
          </li>
          <li>
            <strong>Prefix / suffix.</strong> Many scanners append a carriage return or tab on every
            read. That&apos;s fine for keyboard-wedge workflows; less fine when you&apos;re piping
            the raw payload into a regex.
          </li>
          <li>
            <strong>Symbology identifier.</strong> Scanners can be set to prefix each read with an
            identifier: <code>]C1</code> for a symbol that starts with FNC1 (GS1-128) and <code>]C0</code> for plain
            Code 128. Useful for catching mis-formatted symbols upstream.
          </li>
        </ol>
        <p>
          If you have a GS1 payload and want to see what each part means, the GS1 decoder splits it
          into Application Identifiers in one step:
        </p>
        <TryTool
          to="/gs1-digital-link-decoder"
          label="GS1 Decoder"
          description="Split a GS1 element string or Digital Link URL into its Application Identifiers."
          value="(01)07640156790009(10)ABC123(17)261231"
          cta="Detect the worked example"
        />
        <p>
          For round-trip QA — scanning your own printed Code 128 to verify it decodes cleanly — use
          the <Link to="/barcode-reader">Online Barcode Reader</Link> from a phone or webcam. For
          bulk validation across hundreds of historical payloads, paste the list into the <Link to="/batch-barcode-scanner">Batch Barcode Tools</Link>.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="common-mistakes" title="Common Mistakes">
        <p>
          The repeat offenders that turn a perfectly good Code 128 into a downstream parsing
          headache:
        </p>
        <ul>
          <li>
            <strong>Splitting GS1-128 AIs on a hard-coded character.</strong> Variable-length fields
            end up concatenated with the next AI when FNC1 is stripped. Always split on the
            configured FNC1 replacement (typically ASCII 29).
          </li>
          <li>
            <strong>Sending mixed-case text through a Subset A encoder.</strong> Lowercase letters
            silently disappear because Subset A doesn&apos;t cover them. Use Subset B (or let the
            encoder pick).
          </li>
          <li>
            <strong>Printing too narrow.</strong> Code 128 needs a minimum X-dimension
            (single-module width) to print and scan reliably. Shrink below it for label real-estate
            reasons and the symbol grade collapses on the first scratch.
          </li>
          <li>
            <strong>Confusing Code 128 with Code 39.</strong> Code 39 can&apos;t encode lowercase
            and is roughly 2× wider for the same payload. If your &quot;Code 128&quot; looks twice
            as long as the same data printed elsewhere, it&apos;s probably Code 39.
          </li>
          <li>
            <strong>Assuming GS1-128 implies a specific AI order.</strong> AIs can appear in any
            order — what matters is the FNC1 after every variable-length one. Reorder them all you
            like during artwork QA; the parser doesn&apos;t care.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="quick-decision" title="Quick Decision Guide">
        <p>
          When you only need the practical answer for a new label, the choices collapse to four
          short ones:
        </p>
        <ul>
          <li>
            <strong>Choose Code 128</strong> when the payload is alphanumeric, you don&apos;t need
            GS1 structured data, and the label has room for a 1D symbol — internal asset tags,
            shipping labels, asset stickers.
          </li>
          <li>
            <strong>Choose GS1-128</strong> when the payload carries GS1 Application Identifiers —
            SSCC pallet IDs, GTIN+batch+expiry on cartons, traceability data inside the logistics
            label.
          </li>
          <li>
            <strong>Avoid Code 39</strong> unless you&apos;re forced to by a legacy reader.
            It&apos;s wider than Code 128 for the same data and can&apos;t carry lowercase.
          </li>
          <li>
            <strong>Move to 2D</strong> (Data Matrix or QR) when the payload pushes past ~40
            characters, when the label is square rather than wide, or when you want the
            Sunrise-2027-ready GS1 Digital Link form on the consumer pack.
          </li>
        </ul>
      </GuideArticleSection>

      <GuideArticleSection id="code-128-vs-code-39" title="Code 128 vs Code 39">
        <p>
          Code 128 is the better choice for new labels: it is narrower for the same data, covers all
          of ASCII and always carries a check character. Code 39 only makes sense where an older
          scanner or an industry specification still requires it.
        </p>
        <TableScroll>
          <table>
            <caption className="sr-only">
              How Code 128 and Code 39 build their symbols: character structure, spacing, start and
              stop, check character, full ASCII and governing standard.
            </caption>
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col">Code 128</th>
                <th scope="col">Code 39</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">One character</th>
                <td>11 modules: 3 bars and 3 spaces, each 1 to 4 modules wide</td>
                <td>9 elements: 5 bars and 4 spaces, 3 of them wide</td>
              </tr>
              <tr>
                <th scope="row">Between characters</th>
                <td>No gap (continuous)</td>
                <td>A narrow gap (discrete)</td>
              </tr>
              <tr>
                <th scope="row">Start and stop</th>
                <td>Start A, B or C, then a 13-module stop pattern</td>
                <td>An asterisk (*) at both ends</td>
              </tr>
              <tr>
                <th scope="row">Check character</th>
                <td>Mandatory, modulo 103</td>
                <td>Optional, modulo 43</td>
              </tr>
              <tr>
                <th scope="row">Full ASCII</th>
                <td>Built in</td>
                <td>Through two-character pairs (Full ASCII Code 39)</td>
              </tr>
              <tr>
                <th scope="row">Standard</th>
                <td>ISO/IEC 15417</td>
                <td>ISO/IEC 16388</td>
              </tr>
            </tbody>
          </table>
        </TableScroll>
        <p>
          Full ASCII Code 39 reaches lowercase and control characters by pairing $, /, + or % with
          a letter, so each of those characters takes two symbol characters and twice the width. A
          printed Code 39 often gives itself away in the human-readable line, which shows the
          asterisks of the start and stop characters.
        </p>
        <p>
          The <Link to="/barcode-generator">Barcode Generator</Link> makes both symbologies, so you can print one uppercase value as Code 128 and as Code 39 and compare the widths.
        </p>
      </GuideArticleSection>

      <GuideArticleSection id="examples" title="Code 128 Examples">
        <p>
          These two Code 128 examples show how an encoder builds a Code 128 barcode and its check
          character, first for digits in Subset C and then for mixed-case text in Subset B.
        </p>
        <p>
          Take the digits 123456. A Code 128 C encoder pairs them as 12, 34 and 56, and each
          pair&apos;s value is the number itself; Start C has the value 105.
        </p>
        <p>
          The check character is the start value plus each data value multiplied by its position,
          modulo 103: 105 + 12×1 + 34×2 + 56×3 = 353, and 353 mod 103 = 44. The finished symbol is
          68 modules wide before quiet zones, against 101 modules for the same six digits in Subset B.
        </p>

        <h3>Code 128 B Example</h3>
        <p>
          Subset B covers lowercase, so a value like Box-7 is encoded one character at a time. Each
          character&apos;s value is its ASCII code minus 32.
        </p>
        <ol>
          <li>Start B has the value 104.</li>
          <li>The data values are B = 34, o = 79, x = 88, the hyphen = 13 and 7 = 23.</li>
          <li>The weighted sum is 104 + 34×1 + 79×2 + 88×3 + 13×4 + 23×5 = 727.</li>
          <li>727 mod 103 = 6, so the check character has the value 6.</li>
          <li>
            Seven symbol characters of 11 modules each plus the 13-module stop give 90 modules, with
            a quiet zone of at least 10 modules on each side.
          </li>
        </ol>
        <BarcodeFigure
          format="code128"
          value="Box-7"
          formatLabel="Code 128"
          caption={
            <>
              Code 128 B example: <code>Box-7</code> encoded in Subset B.
            </>
          }
        />
        <p>
          A scanner returns only Box-7, because the start, check and stop characters never appear in the decoded text. To print a run of labels like this, the <Link to="/bulk-barcode-generator">bulk barcode generator</Link> turns a list of up to 200 values into Code 128 symbols at once.
        </p>
      </GuideArticleSection>

      <GuideKeyTakeaways
        items={[
          "Code 128 encodes the full 128-character ASCII set in three internal subsets; decoders return plain ASCII either way.",
          "GS1-128 is Code 128 with a mandatory FNC1 at the start — same symbology, structured GS1 AI payload.",
          "FNC1 also separates variable-length AIs from whatever follows. Strip it on the wire and the parser glues the fields together.",
          "Code 128 lives in logistics and asset tracking, not on the retail shelf — that's still EAN / UPC territory.",
          "Most Code 128 bugs are scanner configuration, not the barcode itself: send FNC1 as ASCII 29 and the GS1 parser does the rest.",
        ]}
      />


      <GuideArticleSection id="references" title="References">
        <ul>
          <li>
            ISO/IEC 15417:2007 — the international standard that defines the Code 128 symbology
            (subsets, FNC1, checksum algorithm). Available from the <a href="https://www.iso.org/standards.html" rel="noopener noreferrer" target="_blank">
              ISO catalogue
            </a>
            .
          </li>
          <li>
            GS1 General Specifications — defines GS1-128, the Application Identifier table and the
            parsing rules. Published by <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              GS1
            </a> and updated annually.
          </li>
          <li>
            GS1 Logistics Label guide — the practical reference for SSCC, AI ordering and label
            layout on cases and pallets: <a
              href="https://www.gs1.org/standards/barcodes"
              rel="noopener noreferrer"
              target="_blank"
            >
              gs1.org/standards/barcodes
            </a>
            .
          </li>
        </ul>
      </GuideArticleSection>

      <p>
        Need to generate Code 128 or GS1-128 labels yourself? The <Link to="/barcode-generator">Barcode Generator</Link> covers plain Code 128, and the <Link to="/gs1-digital-link-generator">GS1 Digital Link Generator</Link> builds a Digital
        Link URL from the same GTIN + AIs in case you&apos;re also planning a 2D carrier on the
        consumer pack.
      </p>

      <FaqSection heading="Frequently Asked Questions" items={FAQ} />
    </GuideLayout>
  );
}
